import { type FormEvent, useEffect, useState } from "react";
import { postInquiry } from "./inquiry.mjs";
import { ResponsiveImage } from "./ResponsiveImage";
import ServicePage from "./ServicePage";
import { servicePages } from "./service-content";
import { businessQuestions, pageMetadata } from "./seo";
import {
  ArrowDownRight,
  ArrowRight,
  ArrowUpRight,
  ChevronDown,
  Clock3,
  MapPin,
  Mail,
  Menu,
  Phone,
  ShieldCheck,
  Sparkles,
  X,
} from "lucide-react";

const phone = "614-332-0622";
const phoneLink = "+16143320622";
const email = "support@housespace.us";

const services = [
  {
    number: "01",
    title: "Bathroom remodeling",
    description: "Thoughtful updates, durable finishes, and a bathroom that feels like yours.",
    image: "bathroom",
    alt: "Bathroom with a double vanity and shower",
  },
  {
    number: "02",
    title: "Backyard porches",
    description: "Outdoor spaces planned around your property and the way you want to use them.",
    image: "porch",
    alt: "Outdoor seating area on a wooden deck",
  },
  {
    number: "03",
    title: "Epoxy flooring",
    description: "Hard-wearing, easy-to-clean floors for homes, businesses, and industrial spaces.",
    image: "epoxy",
    alt: "Glossy floor in an industrial interior",
  },
  {
    number: "04",
    title: "Custom epoxy tables",
    description: "One-of-a-kind resin tables that bring natural wood and craftsmanship together.",
    image: "table",
    alt: "Wood and resin tabletop with decorative flowers",
  },
  {
    number: "05",
    title: "Landscaping",
    description: "Outdoor spaces planned to feel welcoming, useful, and connected to your home.",
    image: "landscaping",
    alt: "Garden beds with shrubs and flowering plants",
  },
  {
    number: "06",
    title: "Cleaning & restoration",
    description: "Professional cleanup and restoration that help a space feel cared for again.",
    image: "restoration",
    alt: "Worker carrying tools during an interior renovation",
  },
  {
    number: "07",
    title: "Floor stripping & waxing",
    description: "Restore the shine and protect busy residential and commercial floors.",
    image: "floor-care",
    alt: "Person buffing a polished floor",
  },
] as const;

const serviceAreas = [
  {
    city: "Columbus",
    detail: "Our home base",
    image: "columbus",
  },
  {
    city: "Dayton",
    detail: "Montgomery County & beyond",
    image: "dayton",
  },
  {
    city: "Cincinnati",
    detail: "Greater Cincinnati area",
    image: "cincinnati",
  },
] as const;

const steps = [
  {
    number: "01",
    title: "Tell us what you have in mind",
    description: "Share a few details and the best way to reach you. We’ll take it from there.",
  },
  {
    number: "02",
    title: "Get a clear estimate",
    description: "We’ll talk through your space, priorities, and a straightforward next step.",
  },
  {
    number: "03",
    title: "Let’s make it happen",
    description: "Our crew gets to work with care, clear communication, and quality materials.",
  },
];

type FormState = "idle" | "sending" | "success" | "error";

function PrivacyPolicy() {
  return (
    <>
      <a className="skip-link" href="#privacy-content">Skip to content</a>
      <div className="announcement"><span className="announcement-dot" /> HouseSpace privacy</div>
      <header className="site-header privacy-header">
        <a className="wordmark" href="/" aria-label="HouseSpace home">
          <img className="wordmark-icon" src="/brand/house-mark.png" alt="" />
          <span className="wordmark-type">House<span>Space</span></span>
        </a>
        <a className="button button-small button-dark" href="/#estimate">Contact HouseSpace <ArrowUpRight size={16} /></a>
      </header>
      <main id="privacy-content" className="privacy-page section-pad" tabIndex={-1}>
        <p className="eyebrow eyebrow-dark"><span /> YOUR INFORMATION</p>
        <h1>Privacy policy</h1>
        <p className="privacy-review-note">HouseSpace is operated by House Space Design LLC, based in Columbus, Ohio. This notice explains how HouseSpace handles information submitted through this website and how to contact us about your information.</p>
        <section>
          <h2>Information submitted through this site</h2>
          <p>The estimate form requires your name, email address, and project type. Your phone number, project location, and project details are optional. These details are used to respond to your inquiry and discuss your project. Please do not include payment details, government identification numbers, or other sensitive information.</p>
        </section>
        <section>
          <h2>How inquiries are handled</h2>
          <p>The website sends estimate requests to HouseSpace’s contact inbox through an email delivery provider. If sending fails, the form shows an error and provides phone and email alternatives. HouseSpace limits access to staff who need the information for project follow-up. The website does not keep an inquiry database or intentionally log inquiry contents.</p>
        </section>
        <section>
          <h2>Cookies, analytics, and sharing</h2>
          <p>HouseSpace uses inquiry information for project follow-up, does not sell it, and does not use it for unrelated marketing.</p>
          <p>This website does not include advertising or analytics scripts or set application tracking cookies. It uses a temporary, pseudonymous network identifier in server memory to limit abusive requests. Hosting and email service providers may process inquiry data and technical information, such as IP addresses and request metadata, to operate and protect their services. Provider records are separate from the website’s temporary abuse-prevention records.</p>
        </section>
        <section>
          <h2>Retention and deletion</h2>
          <p>HouseSpace’s policy is to delete inactive inquiries that do not become projects 90 days after the last substantive contact, except where a legal obligation or hold requires retention. Accepted-project records follow a separate schedule based on project delivery, warranty, tax, and legal requirements. Provider recovery and backup copies may remain for their retention periods; these exceptions are documented as part of the retention process.</p>
          <p>The website’s abuse-prevention records use a 15-minute request window and are checked for expiry every minute, including when no new requests arrive. These temporary records are separate from inquiry emails and provider records.</p>
        </section>
        <section>
          <h2>Questions</h2>
          <p>For a question about a project inquiry, or to request access to, correction of, or deletion of information you provided, email <a href={`mailto:${email}`}>{email}</a>. Identify the inquiry and your request, but do not send sensitive identity documents. HouseSpace may use proportionate checks to verify the request and will handle it under applicable requirements, explaining any retention obligation or other limitation.</p>
        </section>
        <a className="text-link privacy-back" href="/">Back to HouseSpace <ArrowRight size={16} /></a>
      </main>
    </>
  );
}

export default function App({ pathname = typeof window === "undefined" ? "/" : window.location.pathname }: { pathname?: string }) {
  const metadata = pageMetadata(pathname);
  const isPrivacy = metadata.privacy;
  const service = servicePages.find((item) => metadata.pathname === `/services/${item.slug}/`);
  const [menuOpen, setMenuOpen] = useState(false);
  const [formState, setFormState] = useState<FormState>("idle");

  useEffect(() => {
    const { title, description } = metadata;
    document.title = title;
    const setMeta = (selector: string, attribute: "name" | "property", key: string, content: string) => {
      let meta = document.head.querySelector<HTMLMetaElement>(selector);
      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute(attribute, key);
        document.head.append(meta);
      }
      meta.content = content;
    };
    setMeta('meta[name="description"]', "name", "description", description);
    setMeta('meta[property="og:title"]', "property", "og:title", title);
    setMeta('meta[property="og:description"]', "property", "og:description", description);
    setMeta('meta[property="og:type"]', "property", "og:type", "website");
    setMeta('meta[property="og:url"]', "property", "og:url", metadata.url);
    setMeta('meta[property="og:image"]', "property", "og:image", metadata.image);
    setMeta('meta[property="og:site_name"]', "property", "og:site_name", "HouseSpace");
    setMeta('meta[property="og:locale"]', "property", "og:locale", "en_US");
    setMeta('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");
    setMeta('meta[name="twitter:title"]', "name", "twitter:title", title);
    setMeta('meta[name="twitter:description"]', "name", "twitter:description", description);
    setMeta('meta[name="twitter:image"]', "name", "twitter:image", metadata.image);
    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement("link"); canonical.rel = "canonical"; document.head.append(canonical); }
    canonical.href = metadata.url;
  }, [metadata.pathname]);

  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape" && menuOpen) {
        setMenuOpen(false);
        document.getElementById("mobile-navigation-toggle")?.focus();
      }
    }
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [menuOpen]);

  useEffect(() => {
    if (isPrivacy || !window.location.hash) return;
    let cancelled = false;
    // Direct privacy-to-estimate links arrive before React has mounted the target.
    void document.fonts.ready.then(() => {
      if (!cancelled) document.getElementById(window.location.hash.slice(1))?.scrollIntoView();
    });
    return () => { cancelled = true; };
  }, [isPrivacy]);

  if (isPrivacy) return <PrivacyPolicy />;
  if (service) return <ServicePage service={service} />;

  async function submitInquiry(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormState("sending");
    const form = event.currentTarget;
    const values = Object.fromEntries(new FormData(form).entries());

    try {
      await postInquiry(values);
      setFormState("success");
      form.reset();
    } catch {
      setFormState("error");
    }
  }

  function closeMenu() {
    setMenuOpen(false);
  }

  return (
    <>
      <a className="skip-link" href="#top">Skip to content</a>
      <div className="announcement">
        <span className="announcement-dot" />
        Built for Ohio homes, businesses & everyday life
      </div>
      <header className="site-header">
        <a className="wordmark" href="#top" aria-label="HouseSpace home" onClick={closeMenu}>
          <img className="wordmark-icon" src="/brand/house-mark.png" alt="" />
          <span className="wordmark-type">House<span>Space</span></span>
        </a>
        <button
          id="mobile-navigation-toggle"
          className="mobile-menu-toggle"
          type="button"
          aria-label={menuOpen ? "Close navigation" : "Open navigation"}
          aria-expanded={menuOpen}
          aria-controls="primary-navigation"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          {menuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
        <nav id="primary-navigation" className={menuOpen ? "main-nav is-open" : "main-nav"} aria-label="Main navigation">
          <a href="#services" onClick={closeMenu}>What we do</a>
          <a href="#why-housespace" onClick={closeMenu}>Why HouseSpace</a>
          <a href="#service-area" onClick={closeMenu}>Service area</a>
          <a className="nav-phone" href={`tel:${phoneLink}`}><Phone size={15} /> {phone}</a>
          <a className="button button-small button-dark" href="#estimate" onClick={closeMenu}>
            Get a free estimate <ArrowUpRight size={16} />
          </a>
        </nav>
      </header>

      <main id="top" tabIndex={-1}>
        <section className="hero">
          <div className="hero-heading">
            <div className="eyebrow"><span /> COLUMBUS · DAYTON · CINCINNATI</div>
            <h1>Make room for<br /><em>better living.</em></h1>
            <div className="hero-intro">
              <p className="hero-description">
                Construction and remodeling for homes and businesses in Central and Southwest Ohio,
                including Columbus, Dayton, and Cincinnati.
              </p>
              <div className="hero-actions">
                <a className="button button-accent" href="#estimate">
                  Plan your project <ArrowUpRight size={17} />
                </a>
                <a className="text-link" href="#services">Explore our services <ArrowDownRight size={16} /></a>
              </div>
            </div>
          </div>
          <div className="hero-visual">
            <ResponsiveImage
              className="hero-image"
              asset="porch"
              sizes="100vw"
              alt="Outdoor seating area on a wooden deck"
              fetchPriority="high"
              decoding="async"
            />
            <div className="hero-image-shade" />
            <div className="hero-image-caption">
              <span>BUILT TO FEEL LIKE YOURS</span>
              <strong>Home starts with<br />the details.</strong>
            </div>
            <div className="hero-photo-note">
              <ShieldCheck size={20} aria-hidden="true" />
              <span><strong>Licensed & insured</strong><small>Confidence from day one</small></span>
            </div>
            <span className="hero-photo-index">PORCH INSPIRATION</span>
          </div>
        </section>

        <section className="trust-strip" aria-label="HouseSpace commitments">
          <div className="trust-item"><MapPin size={25} strokeWidth={1.5} /><span>Based in Columbus<br />Serving Central & Southwest Ohio</span></div>
          <div className="trust-item"><ShieldCheck size={25} strokeWidth={1.5} /><span>Licensed & insured<br />professionals</span></div>
          <div className="trust-item"><Sparkles size={25} strokeWidth={1.5} /><span>Free, no-obligation<br />estimates</span></div>
        </section>

        <section className="services section-pad" id="services">
          <div className="services-heading">
            <p className="eyebrow eyebrow-dark"><span /> HOW WE CAN HELP</p>
            <h2>Good work.<br /><em>Better spaces.</em></h2>
            <p className="services-intro">Thoughtful construction and remodeling for the places where life happens—at home, at work, and everywhere in between.</p>
            <a href="#estimate" className="text-link">Talk through your project <ArrowRight size={16} /></a>
          </div>
          <div className="service-list">
            {services.map((service, index) => (
              <article className={index === 0 ? "service-card service-card-feature" : "service-card"} key={service.number}>
                <div className="service-image-wrap">
                  <ResponsiveImage
                    asset={service.image}
                    sizes={index === 0
                      ? "(max-width: 368px) calc(100vw - 48px), (max-width: 780px) 87vw, (max-width: 1370px) 49.2vw, (max-width: 1662px) calc(777.7px - 7.7vw), 650px"
                      : "(max-width: 368px) calc(100vw - 48px), (max-width: 780px) 87vw, (max-width: 1370px) calc(43.5vw - 14px), (max-width: 1662px) calc(671px - 6.5vw), 563px"}
                    alt={service.alt}
                    loading="lazy"
                    decoding="async"
                  />
                  <span className="service-number">{service.number}</span>
                </div>
                <div className="service-card-copy">
                  <h3><a href={`/services/${servicePages[index].slug}/`}>{service.title}</a></h3>
                  <p>{service.description}</p>
                </div>
                <a className="service-arrow" href={`/services/${servicePages[index].slug}/`} aria-label={`Learn about ${service.title}`}><ArrowUpRight size={19} /></a>
              </article>
            ))}
          </div>
          <p className="services-note">Photographs illustrate our services and offer inspiration. They are not a verified portfolio of HouseSpace projects.</p>
        </section>

        <section className="why-section section-pad" id="why-housespace">
          <div className="why-heading">
            <p className="eyebrow eyebrow-dark"><span /> A TEAM YOU CAN COUNT ON</p>
            <h2>Clear plans.<br /><em>Work that lasts.</em></h2>
            <p className="why-intro">
              HouseSpace helps Ohio homeowners and businesses plan improvements that make
              their spaces more useful, comfortable, and cared for.
            </p>
            <p className="why-intro">
              House Space Design LLC is a licensed Home Improvement General Contractor
              serving Central and Southwest Ohio. Fully insured.
            </p>
          </div>
          <figure className="why-photo">
            <ResponsiveImage
              asset="columbus"
              sizes="(min-width: 1580px) 1370px, (max-width: 780px) calc(100vw - 48px), 87vw"
              alt="Columbus skyline beside the river"
              loading="lazy"
              decoding="async"
            />
            <figcaption><span>LOCAL KNOW-HOW</span><strong>Rooted in Ohio. Ready for your project.</strong><span><MapPin size={15} /> CENTRAL & SOUTHWEST OHIO</span></figcaption>
          </figure>
          <div className="why-points">
            <article><span className="value-index">01</span><h3>Good communication</h3><p>Know what happens next and who to call.</p></article>
            <article><span className="value-index">02</span><h3>Careful craftsmanship</h3><p>Quality materials and attention to the details.</p></article>
          </div>
          <a className="why-link" href="#estimate">Meet us at your project <ArrowUpRight size={17} /></a>
        </section>

        <section className="process section-pad" id="how-it-works">
          <div className="process-intro">
            <p className="eyebrow"><span /> NO GUESSWORK</p>
            <h2>A simple way<br />to <em>get started.</em></h2>
            <p>Big renovation or one well-chosen upgrade, every good project starts with a conversation.</p>
            <a className="process-cta" href="#estimate">Start with a free estimate <ArrowRight size={17} /></a>
          </div>
          <div className="steps-list">
            {steps.map((step, index) => (
              <article className="step-card" key={step.number}>
                <div className="step-number"><span>{step.number}</span></div>
                <div className="step-copy"><h3>{step.title}</h3><p>{step.description}</p></div>
                {index < steps.length - 1 && <span className="step-line" aria-hidden="true" />}
              </article>
            ))}
          </div>
        </section>

        <section className="service-area section-pad" id="service-area">
          <div className="area-header">
            <div>
              <p className="eyebrow"><span /> NEAR YOU, HERE IN OHIO</p>
              <h2>Locally based.<br /><em>Ready to help.</em></h2>
            </div>
            <div className="area-summary">
              <p>Based in Columbus and serving residential and commercial clients in Central and Southwest Ohio.</p>
              <div className="area-footer"><Clock3 size={18} /> <span>Monday–Friday: 9:00 AM–5:00 PM<br />Saturday & Sunday: Closed</span></div>
            </div>
          </div>
          <div className="area-register">
            {serviceAreas.map((area, index) => (
              <article className="area-card" key={area.city}>
                <span className="area-number">0{index + 1}</span>
                <div className="area-card-content"><strong>{area.city}</strong><small>{area.detail}</small></div>
                <ResponsiveImage asset={area.image} sizes="(max-width: 560px) 120px, (max-width: 780px) 170px, 225px" alt="" loading="lazy" decoding="async" />
                <span className="area-map-label"><MapPin size={14} /> OHIO</span>
              </article>
            ))}
          </div>
        </section>

        <section className="business-faq section-pad" id="questions" aria-labelledby="questions-heading">
          <p className="eyebrow eyebrow-dark"><span /> BEFORE YOU GET STARTED</p>
          <h2 id="questions-heading">Your questions, <em>answered.</em></h2>
          <p>HouseSpace is operated by House Space Design LLC, based in Columbus, Ohio.</p>
          <div className="business-faq-list">
            {businessQuestions.map((item) => <details key={item.question}><summary>{item.question}</summary><p>{item.answer}</p></details>)}
          </div>
        </section>

        <section className="estimate section-pad" id="estimate">
          <div className="estimate-heading">
            <div>
              <p className="eyebrow eyebrow-dark"><span /> YOUR NEXT STEP</p>
              <h2>Let’s make <em>something better.</em></h2>
            </div>
            <p>Tell us a little about your project. We’ll reach out to talk through the details and your free estimate.</p>
          </div>
          <div className="estimate-contact">
            <a href={`tel:${phoneLink}`}><Phone size={18} /><span><small>CALL OUR TEAM</small>{phone}</span><ArrowUpRight size={15} /></a>
            <a href={`mailto:${email}`}><Mail size={18} /><span><small>EMAIL OUR TEAM</small>{email}</span><ArrowUpRight size={15} /></a>
          </div>
          <div className="estimate-form-card">
            <div className="form-heading"><div><span>01</span><h3>Tell us about it</h3></div><span className="form-required">* Required</span></div>
            <form onSubmit={submitInquiry} aria-busy={formState === "sending"}>
              <label className="honeypot" aria-hidden="true">
                Leave this field empty
                <input name="website" tabIndex={-1} autoComplete="off" />
              </label>
              <div className="form-row">
                <label>Your name *<input name="name" required minLength={2} maxLength={100} placeholder="How should we address you?" autoComplete="name" /></label>
                <label>Email address *<input name="email" type="email" required maxLength={254} placeholder="you@example.com" autoComplete="email" /></label>
              </div>
              <div className="form-row">
                <label>Phone number<input name="phone" type="tel" maxLength={30} placeholder="(555) 555-5555" autoComplete="tel" /></label>
                <label>What are you planning? *
                  <span className="select-wrap"><select name="service" required defaultValue=""><option value="" disabled>Select a service</option>{services.map((service) => <option key={service.number} value={service.title}>{service.title}</option>)}<option value="Other project">Other project</option></select><ChevronDown size={15} /></span>
                </label>
              </div>
              <label className="form-full">Project location<input name="location" maxLength={100} placeholder="City or ZIP code" autoComplete="postal-code" /></label>
              <label className="form-full">A few project details<textarea name="message" rows={3} maxLength={2000} placeholder="What would you like to change or improve?" /></label>
              <button className="button button-accent form-submit" type="submit" disabled={formState === "sending"}>
                {formState === "sending" ? "Sending your request…" : "Request a free estimate"} <ArrowUpRight size={17} />
              </button>
              <p className={`form-status ${formState === "success" ? "is-success" : ""}`} aria-live="polite" role="status">
                {formState === "success" && "Thanks — your request is in. We’ll be in touch soon."}
                {formState === "error" && <>The online form is unavailable right now. Call <a href={`tel:${phoneLink}`}>{phone}</a> or email <a href={`mailto:${email}`}>{email}</a>.</>}
              </p>
              <p className="form-privacy">Your details are used to respond to your inquiry and discuss your project. Do not include sensitive information. Read the <a href="/privacy-policy/">privacy notice</a>.</p>
            </form>
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <div className="footer-top">
          <div className="footer-brand">
            <a className="wordmark footer-wordmark" href="#top">
              <img className="wordmark-icon" src="/brand/house-mark.png" alt="" />
              <span className="wordmark-type">House<span>Space</span></span>
            </a>
            <p>Thoughtful construction & remodeling<br />for homes and businesses in Central and Southwest Ohio.</p>
            <p>House Space Design LLC · Columbus, Ohio<br />Licensed & fully insured.</p>
          </div>
          <div className="footer-links">
            <span className="footer-label">Find us</span>
            <a href="https://maps.google.com/?q=1306+West+Broad+St+Columbus+OH+43222" target="_blank" rel="noreferrer">1306 West Broad St<br />Columbus, OH 43222 <ArrowUpRight size={14} /></a>
          </div>
          <div className="footer-links">
            <span className="footer-label">Talk with our team</span>
            <a href={`tel:${phoneLink}`}>{phone} <ArrowUpRight size={14} /></a>
            <a href={`mailto:${email}`}>{email} <ArrowUpRight size={14} /></a>
          </div>
          <div className="footer-links footer-nav">
            <span className="footer-label">Explore</span>
            <a href="#services">What we do</a>
            <a href="#service-area">Service area</a>
          </div>
        </div>
        <div className="footer-bottom">
          <span>© {new Date().getFullYear()} HouseSpace. All rights reserved.</span>
          <a href="/privacy-policy/">Privacy policy</a>
          <a href="#top">Back to top</a>
        </div>
      </footer>
    </>
  );
}
